import { useEffect, useState, useRef } from "react";
import { API_BASE_URL } from "../utils/config";

const CALLS = [
  { path: "/api/auth/me/",           label: "user profile"      },
  { path: "/api/items/?page_size=1", label: "inventory catalog" },
  { path: "/api/categories/",        label: "categories"        },
  { path: "/api/locations/",         label: "locations"         },
];

const CALL_DELAY   = 200;   // ms before first call (after initial log lines)
const STAGGER      = 160;   // ms between each call start
const FETCH_TIMEOUT = 7000; // abort single fetch if nginx hangs
const MIN_SHOW_MS  = 1800;  // minimum splash duration
const DONE_PAUSE   = 650;   // pause after "all services online" before fade
const FADE_MS      = 460;
const MAX_LINES    = 7;
const PCT_STEP     = Math.floor(88 / CALLS.length); // ~22 per call

const getToken = () => localStorage.getItem("accessToken");
const authHdr  = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const S = {
  ok:   { badge: " OK ",  fg: "#22c97a",               bg: "rgba(34,201,122,.15)"  },
  info: { badge: " .. ",  fg: "rgba(255,255,255,.28)",  bg: "rgba(255,255,255,.06)" },
  req:  { badge: " >> ",  fg: "#ffe94a",               bg: "rgba(255,233,74,.14)" },
  warn: { badge: " !! ",  fg: "#f59e0b",               bg: "rgba(245,158,11,.14)"  },
  fail: { badge: "FAIL",  fg: "#f04f5a",               bg: "rgba(240,79,90,.20)"   },
};

/* ── relay nodes ── */
function RelayNodes({ lit, total, offline }) {
  const N = total + 1;
  return (
    <div style={{ display: "flex", alignItems: "center", margin: "16px 0 20px" }}>
      {Array.from({ length: N }, (_, i) => {
        const isLit = !offline && i <= lit;
        const pulse = !offline && i === lit && i < N - 1;
        const edge  = i === 0 || i === N - 1;
        const sz    = edge ? 22 : 14;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center" }}>
            <div style={{
              width: sz, height: sz, borderRadius: "50%",
              border: `1.5px solid ${
                offline ? "rgba(240,79,90,.40)" :
                isLit   ? "rgba(255,233,74,.88)" :
                          "rgba(255,255,255,.09)"}`,
              background: offline ? "rgba(240,79,90,.07)" :
                          isLit   ? "rgba(255,233,74,.12)" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: (!offline && isLit) ? "0 0 8px rgba(255,233,74,.55)" : "none",
              transition: "border-color .5s, background .5s, box-shadow .5s",
              animation: pulse ? "nodeActive 1.4s ease-in-out infinite" : "none",
              willChange: pulse ? "transform, opacity" : "auto",
            }}>
              <div style={{
                width: edge ? 7 : 5, height: edge ? 7 : 5, borderRadius: "50%",
                background: offline ? "rgba(240,79,90,.44)" :
                            isLit   ? "#fff27f" : "rgba(255,255,255,.10)",
                transition: "background .5s",
              }} />
            </div>
            {i < N - 1 && (
              <div style={{
                width: 22, height: 1,
                background: offline             ? "rgba(240,79,90,.14)" :
                            (!offline && i < lit) ? "linear-gradient(90deg,rgba(255,233,74,.70),rgba(255,242,127,.65))" :
                                                  "rgba(255,255,255,.07)",
                transition: "background .5s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── elapsed clock in title bar ── */
function Elapsed({ t0, frozen }) {
  const [e, setE] = useState(0);
  useEffect(() => {
    if (frozen) return;
    const id = setInterval(() => setE(Date.now() - t0), 100);
    return () => clearInterval(id);
  }, [t0, frozen]);
  const s  = Math.floor(e / 1000);
  const cs = String(Math.floor((e % 1000) / 10)).padStart(2, "0");
  return (
    <span style={{ marginLeft: "auto", color: "rgba(255,255,255,.13)", fontSize: 9, fontFamily: "monospace" }}>
      {s}.{cs}s
    </span>
  );
}

/* ── terminal log ── */
function Terminal({ lines, t0, offline }) {
  const vis = lines.slice(-MAX_LINES);
  const pad = MAX_LINES - vis.length;
  return (
    <div style={{
      width: "min(348px, 90vw)",
      background: offline ? "rgba(34,4,7,.62)" : "rgba(0,0,0,.44)",
      border: `1px solid ${offline ? "rgba(240,79,90,.35)" : "rgba(255,255,255,.08)"}`,
      borderRadius: 10, overflow: "hidden",
      boxShadow: offline
        ? "0 0 24px rgba(240,79,90,.14), 0 8px 32px rgba(0,0,0,.55)"
        : "inset 0 1px 0 rgba(255,255,255,.04), 0 8px 32px rgba(0,0,0,.50)",
      transition: "border-color .6s, box-shadow .6s, background .6s",
    }}>
      {/* title bar */}
      <div style={{
        height: 30,
        background: offline ? "rgba(240,79,90,.055)" : "rgba(255,255,255,.03)",
        borderBottom: `1px solid ${offline ? "rgba(240,79,90,.18)" : "rgba(255,255,255,.055)"}`,
        display: "flex", alignItems: "center", padding: "0 12px", gap: 6,
        transition: "background .6s, border-color .6s",
      }}>
        {["rgba(224,82,82,.70)","rgba(224,160,32,.70)","rgba(64,176,96,.70)"].map((c,i) => (
          <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
        ))}
        <span style={{ marginLeft: 8, color: "rgba(255,255,255,.16)", fontSize: 9, fontFamily: "monospace", letterSpacing: ".09em" }}>
          Lisokom — boot.log
        </span>
        <Elapsed t0={t0} frozen={offline} />
      </div>

      {/* log area */}
      <div style={{ position: "relative", height: 152 }}>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 40,
          background: offline
            ? "linear-gradient(to bottom,rgba(34,4,7,.62),transparent)"
            : "linear-gradient(to bottom,rgba(0,0,0,.44),transparent)",
          zIndex: 2, pointerEvents: "none", transition: "background .6s",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
          padding: "8px 13px 10px", gap: 3, overflow: "hidden",
        }}>
          {Array.from({ length: pad }).map((_, i) => (
            <div key={`p${i}`} style={{ height: 17, flexShrink: 0 }} />
          ))}
          {vis.map((ln, i) => {
            const isLast = i === vis.length - 1;
            const age    = vis.length - 1 - i;
            const st     = S[ln.s] || S.info;
            return (
              <div key={ln.id} style={{
                display: "flex", alignItems: "center", flexShrink: 0,
                height: 17, overflow: "hidden",
                opacity: Math.max(0.10, 1 - age * 0.13),
                animation: isLast ? "lineIn .18s cubic-bezier(.22,1,.36,1) both" : "none",
                willChange: isLast ? "transform, opacity" : "auto",
              }}>
                <span style={{
                  color: "rgba(255,255,255,.13)", fontSize: 8.5, fontFamily: "monospace",
                  marginRight: 7, flexShrink: 0, minWidth: 26,
                }}>
                  {ln.ts}
                </span>
                <span style={{
                  color: st.fg, background: st.bg, borderRadius: 3,
                  padding: "0.5px 3px", fontSize: 7.5, letterSpacing: ".05em",
                  fontFamily: "monospace", marginRight: 7, flexShrink: 0, lineHeight: "14px",
                }}>
                  {st.badge}
                </span>
                <span style={{
                  color: ln.s === "ok"   ? "rgba(255,255,255,.62)"
                       : ln.s === "req"  ? "rgba(255,242,127,.72)"
                       : ln.s === "fail" ? "rgba(240,90,90,.92)"
                       : ln.s === "warn" ? "rgba(245,158,11,.84)"
                       : "rgba(255,255,255,.35)",
                  fontSize: 10, fontFamily: "monospace",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  lineHeight: "17px",
                }}>
                  {ln.t}
                  {isLast && !offline && (
                    <span style={{ animation: "blink .9s step-end infinite", marginLeft: 3 }}>▋</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── offline banner ── */
function OfflineBanner() {
  return (
    <div style={{
      marginTop: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
      animation: "fadeUp .38s ease both",
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: "50%",
        border: "1.5px solid rgba(240,79,90,.50)",
        background: "rgba(240,79,90,.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: "offlineGlow 2.2s ease-in-out infinite",
      }}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <line x1="4.5" y1="4.5" x2="13.5" y2="13.5" stroke="#f04f5a" strokeWidth="2" strokeLinecap="round"/>
          <line x1="13.5" y1="4.5" x2="4.5"  y2="13.5" stroke="#f04f5a" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        color: "rgba(240,79,90,.62)", fontSize: 10,
        fontFamily: "monospace", letterSpacing: ".12em",
      }}>
        BACKEND UNREACHABLE
      </span>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "7px 22px", fontSize: 11, fontWeight: 600,
          fontFamily: "system-ui,-apple-system,sans-serif", letterSpacing: ".04em",
          color: "rgba(255,255,255,.55)", background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.12)", borderRadius: 7, cursor: "pointer",
          transition: "background .2s, color .2s, border-color .2s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background   = "rgba(255,255,255,.10)";
          e.currentTarget.style.color        = "rgba(255,255,255,.82)";
          e.currentTarget.style.borderColor  = "rgba(255,255,255,.25)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background   = "rgba(255,255,255,.05)";
          e.currentTarget.style.color        = "rgba(255,255,255,.55)";
          e.currentTarget.style.borderColor  = "rgba(255,255,255,.12)";
        }}
      >
        Retry
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Main SplashScreen
   Real process — no scripted animation timers.
   Progress + log driven entirely by actual API calls.
══════════════════════════════════════════════════════ */
export default function SplashScreen({ onDone }) {
  const [pct,     setPct]     = useState(5);
  const [lit,     setLit]     = useState(0);
  const [lines,   setLines]   = useState([]);
  const [offline, setOffline] = useState(false);
  const [fading,  setFading]  = useState(false);

  const t0     = useRef(Date.now());
  const lineId = useRef(0);
  const tmrs   = useRef([]);
  const pctRef = useRef(5); // mirrors pct — readable inside async closures

  useEffect(() => {
    let cancelled = false;
    let downCount = 0; // calls that failed with gateway/network error
    let settled   = 0; // calls that completed (any result)

    const later = (fn, ms) => {
      const id = setTimeout(fn, ms);
      tmrs.current.push(id);
      return id;
    };

    const ts = () => {
      const el = Date.now() - t0.current;
      return `${String(Math.floor(el / 1000)).padStart(2,"0")}.${String(Math.floor((el % 1000) / 10)).padStart(2,"0")}`;
    };

    const addLine = (s, t) => {
      if (cancelled) return;
      setLines(prev => [...prev, { id: lineId.current++, s, t, ts: ts() }]);
    };

    const bumpPct = () => {
      pctRef.current = Math.min(95, pctRef.current + PCT_STEP);
      if (!cancelled) setPct(pctRef.current);
    };

    const finish = () => {
      if (cancelled) return;
      setPct(100);
      const elapsed = Date.now() - t0.current;
      const wait    = Math.max(0, MIN_SHOW_MS - elapsed) + DONE_PAUSE;
      later(() => {
        if (cancelled) return;
        setFading(true);
        later(() => { if (!cancelled) onDone(); }, FADE_MS);
      }, wait);
    };

    const goOffline = () => {
      if (cancelled) return;
      setOffline(true);
      later(() => addLine("fail", `Backend unreachable — ${downCount}/${CALLS.length} services down`), 260);
    };

    const onSettled = () => {
      if (cancelled) return;
      settled++;
      if (settled < CALLS.length) return;

      if (downCount === CALLS.length) {
        goOffline();
      } else {
        const ok = CALLS.length - downCount;
        // Small delay so last call's log line renders before summary
        later(() => {
          if (cancelled) return;
          if (downCount > 0) {
            addLine("warn", `${ok}/${CALLS.length} services available`);
          } else {
            addLine("ok", "All services online ✓");
          }
          finish();
        }, 180);
      }
    };

    // ── initial log lines ──
    addLine("info", "Runtime initialized");
    later(() => addLine("info", "Connecting to backend..."), 100);

    const tok = getToken();
    if (!tok) {
      later(() => { if (!cancelled) onDone(); }, 400);
      return () => { cancelled = true; tmrs.current.forEach(clearTimeout); };
    }

    // ── real API calls ──
    CALLS.forEach(({ path, label }, i) => {
      later(async () => {
        if (cancelled) return;
        addLine("req", `GET ${path.split("?")[0]}`);

        let success = false;
        try {
          const ctrl  = new AbortController();
          const tAbrt = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT);
          const res   = await fetch(`${API_BASE_URL}${path}`, {
            headers: authHdr(), signal: ctrl.signal,
          });
          clearTimeout(tAbrt);

          if (res.status === 502 || res.status === 503 || res.status === 504) {
            // nginx can't reach backend container
            downCount++;
            addLine("warn", `${label} — gateway error (${res.status})`);
          } else if (!res.ok) {
            // Auth/other HTTP error — backend IS alive
            addLine("warn", `${label} — HTTP ${res.status}`);
          } else {
            const data = await res.json();
            let detail = "";
            if (data?.username)                       detail = ` — ${data.username}`;
            else if (typeof data?.count === "number") detail = ` — ${data.count} records`;
            else if (Array.isArray(data))             detail = ` — ${data.length} items`;
            success = true;
            if (!cancelled) {
              setLit(n => n + 1);
              setLines(prev => [...prev, { id: lineId.current++, s: "ok", t: `${label} loaded${detail}`, ts: ts() }]);
            }
          }
        } catch (err) {
          if (cancelled) return;
          if (err.name === "TypeError") downCount++; // direct connection refused
          const reason = err.name === "AbortError" ? "timed out"
                       : err.name === "TypeError"  ? "connection refused"
                       : err.message || "error";
          addLine("warn", `${label} — ${reason}`);
        }

        // Advance progress bar for every settled call (success or fail)
        if (!cancelled) bumpPct();
        if (success && !cancelled) {
          // small extra visual bump for successful calls
        }
        onSettled();
      }, CALL_DELAY + i * STAGGER);
    });

    return () => {
      cancelled = true;
      tmrs.current.forEach(clearTimeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style>{`
        @keyframes logoGlow    { 0%,100%{filter:drop-shadow(0 0 14px rgba(255,233,74,.40))} 50%{filter:drop-shadow(0 0 28px rgba(255,233,74,.78))} }
        @keyframes logoGlowRed { 0%,100%{filter:drop-shadow(0 0 14px rgba(240,79,90,.38))} 50%{filter:drop-shadow(0 0 28px rgba(240,79,90,.72))} }
        @keyframes nodeActive  { 0%,100%{opacity:.55;transform:scale(1)} 50%{opacity:1;transform:scale(1.32)} }
        @keyframes bgPulse     { 0%,100%{opacity:.55} 50%{opacity:1} }
        @keyframes bgPulseRed  { 0%,100%{opacity:.40} 50%{opacity:.86} }
        @keyframes lineIn      { from{opacity:0;transform:translateY(7px)} to{opacity:1;transform:translateY(0)} }
        @keyframes blink       { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes fadeUp      { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes offlineGlow { 0%,100%{box-shadow:0 0 18px rgba(240,79,90,.25)} 50%{box-shadow:0 0 32px rgba(240,79,90,.55)} }
        @keyframes splashIn    { from{opacity:0} to{opacity:1} }
      `}</style>

      <div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: offline ? "#120407" : "#04173a",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms cubic-bezier(.4,0,.2,1), background 1s`,
        pointerEvents: fading ? "none" : "all",
        overflow: "hidden", userSelect: "none",
        animation: "splashIn .28s ease both",
        willChange: "opacity",
      }}>

        {/* ambient glow */}
        <div style={{
          position: "absolute", top: "40%", left: "50%",
          transform: "translate(-50%,-50%)",
          width: 500, height: 500,
          background: offline
            ? "radial-gradient(circle,rgba(240,79,90,.10) 0%,transparent 65%)"
            : "radial-gradient(circle,rgba(255,233,74,.08) 0%,transparent 65%)",
          animation: offline ? "bgPulseRed 2.5s ease-in-out infinite" : "bgPulse 3.2s ease-in-out infinite",
          pointerEvents: "none", transition: "background 1.2s",
        }} />

        {/* logo */}
        <img
          src={`${process.env.PUBLIC_URL}/logo_white_1.gif`}
          alt="Lisokom"
          style={{
            width: 88, height: 88, objectFit: "contain", marginBottom: 14,
            animation: offline ? "logoGlowRed 2s ease-in-out infinite" : "logoGlow 2.8s ease-in-out infinite",
            transition: "filter 1s",
          }}
        />

        {/* app name */}
        <div style={{
          color: offline ? "rgba(240,79,90,.55)" : "rgba(255,255,255,.84)",
          fontSize: 11.5, fontWeight: 700, letterSpacing: ".32em",
          textTransform: "uppercase", fontFamily: "system-ui,-apple-system,sans-serif",
          marginBottom: 2, transition: "color .8s",
        }}>
          Lisokom Inventory
        </div>

        <RelayNodes lit={lit} total={CALLS.length} offline={offline} />
        <Terminal   lines={lines} t0={t0.current} offline={offline} />

        {offline ? (
          <OfflineBanner />
        ) : (
          <div style={{ marginTop: 18, width: "min(348px,90vw)" }}>
            <div style={{ height: 2, background: "rgba(255,255,255,.07)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${pct}%`, borderRadius: 99,
                background: "linear-gradient(90deg,#f5d90a,#ffe94a 55%,#fff27f)",
                boxShadow: "0 0 10px rgba(255,233,74,.85), 0 0 22px rgba(255,233,74,.28)",
                transition: "width .7s cubic-bezier(.4,0,.2,1)",
              }} />
            </div>
            <div style={{
              display: "flex", justifyContent: "flex-end",
              marginTop: 5, color: "rgba(255,255,255,.14)", fontSize: 9, fontFamily: "monospace",
            }}>
              {pct}%
            </div>
          </div>
        )}
      </div>
    </>
  );
}
